import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixIntakeFormsV1IntakeForm from './intake-forms-v1-intake-form-intake-forms.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A wrapper around a Wix form that adds expiration tracking and submission management.
 * Intake forms collect information from contacts with time-based validity requirements.
 */
export interface IntakeForm {
  /**
   * Intake form ID. Identical to the [Wix Forms form ID](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object).
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Expiration period in months. If not set, submissions don't expire.
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Whether the intake form is archived and disabled from accepting new submissions.
   * @readonly
   */
  archived?: boolean | null;
  /**
   * Intake form name.
   * @minLength 1
   * @maxLength 100
   * @readonly
   */
  name?: string | null;
  /**
   * Date and time the intake form was created in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the intake form was updated in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the intake form.
   * @readonly
   */
  revision?: string | null;
  /**
   * Expiration period in months applied only to submissions that fill a configured trigger field
   * (`pending_trigger_fields`). It has no effect when `pending_trigger_fields` is empty. When not set, triggered
   * submissions fall back to `expiration_period_in_months`.
   * No relational constraint with the base period — it may be shorter or longer.
   * @internal
   * @min 1
   * @max 60
   */
  triggeredExpirationPeriodInMonths?: number | null;
}

/** A reference to a Wix Forms field that triggers `PENDING_APPROVAL` when filled. */
export interface PendingTriggerField {
  /**
   * ID of the [Wix Forms field](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object) that drives the trigger.
   * Must reference a visible checkbox input field. Configuring an unsupported,
   * hidden, or unknown field via `UpdateIntakeFormSettings` is rejected with `UNSUPPORTED_TRIGGER_FIELD`.
   * Stored as the stable field ID (not the translatable label).
   * @format GUID
   */
  formFieldId?: string;
}

/** How an intake form decides which of its submissions require manual approval before becoming valid. */
export enum ApprovalMode {
  /**
   * All submissions are approved automatically; no manual review.
   * @documentationMaturity preview
   * @internal
   */
  AUTO_APPROVE_ALL = 'AUTO_APPROVE_ALL',
  /**
   * Every new submission requires manual approval (`PENDING_APPROVAL`), regardless of answers.
   * @documentationMaturity preview
   * @internal
   */
  MANUAL_APPROVE_ALL = 'MANUAL_APPROVE_ALL',
  /**
   * Only submissions that fill a configured `pending_trigger_fields` entry require manual approval.
   * @documentationMaturity preview
   * @internal
   */
  MANUAL_APPROVE_TRIGGER_FIELDS = 'MANUAL_APPROVE_TRIGGER_FIELDS',
}

/** @enumType */
export type ApprovalModeWithLiterals = ApprovalMode;

export interface QueryIntakeFormsRequest {
  /**
   * Filter object.
   * See @API Query Language
   * for more information.
   *
   * For a detailed list of supported filters, see
   * @Supported Filters.
   */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryIntakeFormsResponse {
  /** Retrieved intake forms. */
  intakeForms?: IntakeForm[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateIntakeFormExpirationPeriodRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * New expiration period in months. If not set, submissions don't expire.
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

export interface UpdateIntakeFormExpirationPeriodResponse {
  /** Updated intake form. */
  intakeForm?: IntakeForm;
}

export interface UpdateIntakeFormSettingsRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId?: string;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision?: string;
  /**
   * Base expiration period in months. Omit to leave unchanged; explicit null clears it (submissions don't expire).
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Expiration period in months applied only to submissions that fire a trigger field. Has no effect when `pending_trigger_fields` is empty. Omit to leave unchanged; explicit null clears it (triggered submissions fall back to the base period).
   * @min 1
   * @max 60
   */
  triggeredExpirationPeriodInMonths?: number | null;
}

export interface UpdateIntakeFormSettingsResponse {
  /** Updated intake form. */
  intakeForm?: IntakeForm;
}

export interface CreateCustomerSubmissionLinkRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * [Contact](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/introduction) ID to personalize and pre-fill the submission with the contact's information.
   * @format GUID
   */
  contactId?: string | null;
}

export interface CreateCustomerSubmissionLinkResponse {
  /**
   * Generated link URL for the intake form submission.
   * @minLength 1
   * @maxLength 2048
   */
  intakeFormUrl?: string | null;
  /**
   * Encrypted token containing contact details. Available only if you specified `contactId` in request.
   * @maxLength 2048
   */
  token?: string | null;
}

export interface DeleteIntakeFormRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
}

export interface DeleteIntakeFormResponse {}

export interface ArchiveIntakeFormRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

export interface ArchiveIntakeFormResponse {
  /** Archived intake form. */
  intakeForm?: IntakeForm;
}

export interface UnarchiveIntakeFormRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

export interface UnarchiveIntakeFormResponse {
  /** Unarchived intake form. */
  intakeForm?: IntakeForm;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface DecryptContactDetailsFromTokenRequest {
  /**
   * Encrypted token containing contact details and creation timestamp.
   * Generated by [Create Customer Submission Link](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-forms/create-customer-submission-link) when a `contactId` is provided.
   * @minLength 1
   * @maxLength 2048
   */
  token?: string;
}

export interface DecryptContactDetailsFromTokenResponse {
  /** Contact details extracted from the token. */
  contactDetails?: ContactDetails;
}

export interface ContactDetails {
  /**
   * Contact email address. Always present.
   * @format EMAIL
   */
  email?: string;
  /**
   * Contact first name. Present when the contact record has a first name.
   * @maxLength 200
   */
  firstName?: string | null;
  /**
   * Contact last name. Present when the contact record has a last name.
   * @maxLength 200
   */
  lastName?: string | null;
}

/** @docsIgnore */
export type CreateCustomerSubmissionLinkApplicationErrors = {
  code?: 'MISSING_SITE_URL';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Retrieves a list of intake forms, with the specified paging, filtering, and sorting.
 *
 * Query Intake Forms runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * Learn more about [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 * @public
 * @permissionId INTAKE_FORM.INTAKE_FORM_READ
 * @applicableIdentity APP
 * @fqn wix.intakeforms.v1.IntakeFormsService.QueryIntakeForms
 */
export function queryIntakeForms(): IntakeFormsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    IntakeForm,
    'CURSOR',
    QueryIntakeFormsRequest,
    QueryIntakeFormsResponse
  >({
    func: async (payload: QueryIntakeFormsRequest) => {
      const reqOpts =
        ambassadorWixIntakeFormsV1IntakeForm.queryIntakeForms(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryIntakeFormsRequest['query']) => {
      const args = [query, {}] as [QueryIntakeFormsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryIntakeFormsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.intakeForms,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface IntakeFormsQueryResult extends QueryCursorResult {
  items: IntakeForm[];
  query: IntakeFormsQueryBuilder;
  next: () => Promise<IntakeFormsQueryResult>;
  prev: () => Promise<IntakeFormsQueryResult>;
}

export interface IntakeFormsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name',
    value: string
  ) => IntakeFormsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName: '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate',
    value: any[]
  ) => IntakeFormsQueryBuilder;
  in: (
    propertyName: '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate',
    value: any
  ) => IntakeFormsQueryBuilder;
  exists: (
    propertyName: '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate',
    value: boolean
  ) => IntakeFormsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate'
    >
  ) => IntakeFormsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      '_id' | 'archived' | 'name' | '_createdDate' | '_updatedDate'
    >
  ) => IntakeFormsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => IntakeFormsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => IntakeFormsQueryBuilder;
  find: () => Promise<IntakeFormsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.intakeforms.v1.IntakeFormsService.QueryIntakeForms
 * @requiredField query
 */
export async function typedQueryIntakeForms(
  query: IntakeFormQuery
): Promise<NonNullablePaths<QueryIntakeFormsResponse, `intakeForms`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixIntakeFormsV1IntakeForm.queryIntakeForms(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface IntakeFormQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', '_updatedDate', 'archived', 'name'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  IntakeForm,
  IntakeFormQuerySpec
>;
export type IntakeFormQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<IntakeForm, IntakeFormQuerySpec, IntakeFormQuery>(),
  },
};

/**
 * Updates the expiration period of a specific intake form.
 *
 * When you update the expiration period, the system recalculates expiration for all existing submissions.
 * @param intakeFormId - Intake form ID.
 * @public
 * @requiredField intakeFormId
 * @requiredField options.formRevision
 * @permissionId INTAKE_FORM.INTAKE_FORM_UPDATE
 * @applicableIdentity APP
 * @fqn wix.intakeforms.v1.IntakeFormsService.UpdateIntakeFormExpirationPeriod
 */
export async function updateIntakeFormExpirationPeriod(
  intakeFormId: string,
  options?: NonNullablePaths<
    UpdateIntakeFormExpirationPeriodOptions,
    `formRevision`,
    2
  >
): Promise<UpdateIntakeFormExpirationPeriodResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    intakeFormId: intakeFormId,
    expirationPeriodInMonths: options?.expirationPeriodInMonths,
    formRevision: options?.formRevision,
  });

  const reqOpts =
    ambassadorWixIntakeFormsV1IntakeForm.updateIntakeFormExpirationPeriod(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          intakeFormId: '$[0]',
          expirationPeriodInMonths: '$[1].expirationPeriodInMonths',
          formRevision: '$[1].formRevision',
        },
        singleArgumentUnchanged: false,
      },
      ['intakeFormId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateIntakeFormExpirationPeriodOptions {
  /**
   * New expiration period in months. If not set, submissions don't expire.
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

/**
 * Creates a shareable link for customers to submit an intake form.
 *
 * The link is valid for 72 hours.
 * @param intakeFormId - Intake form ID.
 * @public
 * @requiredField intakeFormId
 * @permissionId INTAKE_FORMS.CUSTOMER_SUBMISSION_LINK_CREATE
 * @applicableIdentity APP
 * @fqn wix.intakeforms.v1.IntakeFormsService.CreateCustomerSubmissionLink
 */
export async function createCustomerSubmissionLink(
  intakeFormId: string,
  options?: CreateCustomerSubmissionLinkOptions
): Promise<
  CreateCustomerSubmissionLinkResponse & {
    __applicationErrorsType?: CreateCustomerSubmissionLinkApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    intakeFormId: intakeFormId,
    contactId: options?.contactId,
  });

  const reqOpts =
    ambassadorWixIntakeFormsV1IntakeForm.createCustomerSubmissionLink(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          intakeFormId: '$[0]',
          contactId: '$[1].contactId',
        },
        singleArgumentUnchanged: false,
      },
      ['intakeFormId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateCustomerSubmissionLinkOptions {
  /**
   * [Contact](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/introduction) ID to personalize and pre-fill the submission with the contact's information.
   * @format GUID
   */
  contactId?: string | null;
}

/**
 * Deletes an intake form.
 *
 *
 * Also deletes the underlying [Wix form](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/introduction).
 *
 * Existing [submissions](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-form-submissions/introduction) for the intake form aren't automatically deleted but become orphaned.
 * When [querying submissions](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-form-submissions/query-intake-form-submissions), orphaned submissions aren't returned.
 * @param intakeFormId - Intake form ID.
 * @public
 * @requiredField intakeFormId
 * @permissionId INTAKE_FORM.INTAKE_FORM_DELETE
 * @applicableIdentity APP
 * @fqn wix.intakeforms.v1.IntakeFormsService.DeleteIntakeForm
 */
export async function deleteIntakeForm(intakeFormId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    intakeFormId: intakeFormId,
  });

  const reqOpts =
    ambassadorWixIntakeFormsV1IntakeForm.deleteIntakeForm(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { intakeFormId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['intakeFormId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Archives an intake form, disabling it from accepting new submissions.
 *
 * Existing submissions remain accessible. You can [unarchive](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-forms/unarchive-intake-form) the form later to resume accepting submissions.
 * @param intakeFormId - Intake form ID.
 * @param formRevision - Revision number, which increments by 1 each time the intake form is updated.
 * To prevent conflicting changes, you must specify the current revision when updating the intake form.
 * @public
 * @requiredField formRevision
 * @requiredField intakeFormId
 * @permissionId INTAKE_FORM.INTAKE_FORM_UPDATE
 * @applicableIdentity APP
 * @fqn wix.intakeforms.v1.IntakeFormsService.ArchiveIntakeForm
 */
export async function archiveIntakeForm(
  intakeFormId: string,
  formRevision: string
): Promise<ArchiveIntakeFormResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    intakeFormId: intakeFormId,
    formRevision: formRevision,
  });

  const reqOpts =
    ambassadorWixIntakeFormsV1IntakeForm.archiveIntakeForm(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          intakeFormId: '$[0]',
          formRevision: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['intakeFormId', 'formRevision']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Unarchives an intake form, enabling it to accept new submissions.
 * @param intakeFormId - Intake form ID.
 * @param formRevision - Revision number, which increments by 1 each time the intake form is updated.
 * To prevent conflicting changes, you must specify the current revision when updating the intake form.
 * @public
 * @requiredField formRevision
 * @requiredField intakeFormId
 * @permissionId INTAKE_FORM.INTAKE_FORM_UPDATE
 * @applicableIdentity APP
 * @fqn wix.intakeforms.v1.IntakeFormsService.UnarchiveIntakeForm
 */
export async function unarchiveIntakeForm(
  intakeFormId: string,
  formRevision: string
): Promise<UnarchiveIntakeFormResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    intakeFormId: intakeFormId,
    formRevision: formRevision,
  });

  const reqOpts =
    ambassadorWixIntakeFormsV1IntakeForm.unarchiveIntakeForm(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          intakeFormId: '$[0]',
          formRevision: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['intakeFormId', 'formRevision']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
